import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from '../../utils/toast';
import PasteUploadArea from '../PasteUploadArea';
import UserSelector from '../UserSelector';
import AutocompleteField from '../AutocompleteField';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { createVerificationRecord } from '../../utils/documentVerification';
import { useAuth } from '../../contexts/AuthContext';

const MasterDataForm = ({ visible, onCancel, onSuccess, record }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    clientType: '',
    clientName: '',
    clientAddress: '',
    phoneNumber: {
      countryCode: '+91',
      number: ''
    },
    turnover: 0, // Add turnover field
    country: '',
    state: '',
    region: '',
    pincode: '',
    partners: [],
    contactPersons: [],
    authorizedSignatories: [],
    documents: {},
    isActive: true,
    pendingVerifications: [],
    createdBy: null
  });
  const [clientTypes, setClientTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [indiaStates, setIndiaStates] = useState([]);
  const [indiaRegions, setIndiaRegions] = useState([]);
  const [phoneCountryCodes, setPhoneCountryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadFields, setUploadFields] = useState([]);

  useEffect(() => {
    fetchClientTypes();
    fetchUploadFields();
    fetchCountries();
    fetchIndiaStates();
    fetchPhoneCountryCodes();
    console.log('Current user in MasterDataForm:', user); // Debug log
    if (user?.role === 'admin') {
      console.log('User is admin in MasterDataForm, fetching users...'); // Debug log
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    if (record) {
      console.log(record);
      setFormData({
        ...record,
        clientType: record.clientType || '', // Ensure clientType is properly set
        contactPersons: record.contactPersons.map(contact => ({
          ...contact,
          phoneNumbers: contact.phoneNumbers.join(', ')
        })),
        authorizedSignatories: record.authorizedSignatories || [{
          title: '',
          designationName: '',
          fatherName: ''
        }],
        documents: record.documents || {},
        partners: record.partners || [],
        createdBy: record.createdBy || null
      });
    } else {
      setFormData({
        clientType: '',
        clientName: '',
        clientAddress: '',
        phoneNumber: {
          countryCode: '+91',
          number: ''
        },
        turnover: 0, // Initialize turnover for new records
        country: '',
        state: '',
        region: '',
        pincode: '',
        contactPersons: [{
          title: '',
          name: '',
          phoneNumbers: '',
          email: ''
        }],
        authorizedSignatories: [{
          title: '',
          designationName: '',
          fatherName: ''
        }],
        documents: {},
        isActive: true,
        pendingVerifications: [],
        createdBy: null
      });
    }
  }, [record]);

  const fetchClientTypes = async () => {
    try {
      const response = await api.get('/admin/client-types');
      setClientTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch client types');
    }
  };

  const fetchUploadFields = async () => {
    try {
      const response = await api.get('/admin/upload-fields');
      setUploadFields(response.data);
    } catch (error) {
      console.error('Failed to fetch upload fields');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await api.get('/master-data/countries');
      setCountries(response.data.data);
    } catch (error) {
      console.error('Failed to fetch countries');
    }
  };

  const fetchIndiaStates = async () => {
    try {
      const response = await api.get('/master-data/india-states');
      setIndiaStates(response.data.data);
    } catch (error) {
      console.error('Failed to fetch India states');
    }
  };

  const fetchIndiaRegions = async (state) => {
    try {
      const response = await api.get(`/master-data/india-regions/${encodeURIComponent(state)}`);
      setIndiaRegions(response.data.data);
    } catch (error) {
      console.error('Failed to fetch India regions');
    }
  };

  const fetchPhoneCountryCodes = async () => {
    try {
      const response = await api.get('/master-data/phone-country-codes');
      setPhoneCountryCodes(response.data.data);
    } catch (error) {
      console.error('Failed to fetch phone country codes');
    }
  };

  const handleFileUpload = async (file, documentType, label) => {
    try {
      setUploadProgress(prev => ({ ...prev, [documentType]: 0 }));
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [documentType]: progress }));
        }
      });

      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: {
            fileName: file.name,
            fileUrl: response.data.url,
            uploadedAt: new Date(),
            uploadedBy: response.data.userId,
            label: label
          }
        }
      }));

      // Store pending verification for after document is saved (only for new records)
      if (!record) {
        const verificationData = {
          documentType: 'masterData',
          documentField: documentType,
          fileUrl: response.data.url,
          fileName: file.name,
          fileSize: file.size
        };

        // Store in a way that can be accessed later when saving
        setFormData(prev => ({
          ...prev,
          pendingVerifications: [...(prev.pendingVerifications || []), verificationData]
        }));
      } else {
        // For existing records, create verification immediately
        try {
          const verificationData = {
            documentType: 'masterData',
            documentId: record._id,
            documentField: documentType,
            fileUrl: response.data.url,
            fileName: file.name,
            fileSize: file.size
          };

          const verificationResult = await createVerificationRecord(verificationData);
          
          if (verificationResult.verificationRequired) {
            toast.info('Document uploaded successfully. Verification required before approval.');
          } else {
            toast.success('File uploaded successfully');
          }
        } catch (verificationError) {
          console.error('Verification error:', verificationError);
          toast.success('File uploaded successfully');
        }
      }

      setUploadProgress(prev => ({ ...prev, [documentType]: 100 }));
    } catch (error) {
      console.error(`Failed to upload ${documentType}`, error);
      setErrors(prev => ({
        ...prev,
        [documentType]: 'Failed to upload file'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(formData);
    try {
      setLoading(true);

      const formattedValues = {
        ...formData,
        contactPersons: formData.contactPersons.map(contact => ({
          ...contact,
          phoneNumbers: contact.phoneNumbers.split(',').map(phone => phone.trim())
        })),
        authorizedSignatories: formData.authorizedSignatories,
        documents: formData.documents,
        createdBy: formData.createdBy?._id || null
      };

      let savedRecord;
      if (record) {
        savedRecord = await api.put(`/master-data/${record._id}`, formattedValues);
      } else {
        savedRecord = await api.post('/master-data', formattedValues);
        
        // Create verification records for pending verifications
        if (formData.pendingVerifications && formData.pendingVerifications.length > 0) {
          try {
            for (const verificationData of formData.pendingVerifications) {
              const verificationPayload = {
                ...verificationData,
                documentId: savedRecord.data.data._id
              };
              
              await api.post('/checklist/create-after-save', verificationPayload);
            }
            toast.success('Master data saved successfully. Verification records created.');
          } catch (verificationError) {
            console.error('Error creating verification records:', verificationError);
            toast.success('Master data saved successfully. Some verification records may not have been created.');
          }
        } else {
          toast.success('Master data saved successfully');
        }
      }

      onSuccess();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, field, index = null, section = null) => {
    if (section === 'authorizedSignatories') {
      const newSignatories = [...formData.authorizedSignatories];
      newSignatories[index] = {
        ...newSignatories[index],
        [field]: e.target.value
      };
      setFormData(prev => ({
        ...prev,
        authorizedSignatories: newSignatories
      }));
    } else if (index !== null) {
      const newContactPersons = [...formData.contactPersons];
      newContactPersons[index] = {
        ...newContactPersons[index],
        [field]: e.target.value
      };
      setFormData(prev => ({
        ...prev,
        contactPersons: newContactPersons
      }));
    } else if (field === 'clientType') {
      // Handle clientType specially - store as object with _id
      const selectedClientType = clientTypes.find(type => type._id === e.target.value);
      setFormData(prev => ({
        ...prev,
        [field]: selectedClientType || '',
        // Initialize partners array if LLP or Partnership is selected
        partners: (selectedClientType?.name === 'LLP' || selectedClientType?.name === 'Partnership') 
          ? (prev.partners.length > 0 ? prev.partners : [{ name: '' }])
          : []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value
      }));
    }
  };

  const handleUserChange = (selectedUser) => {
    setFormData(prev => ({
      ...prev,
      createdBy: selectedUser
    }));
  };

  const handlePhoneNumberChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: {
        ...prev.phoneNumber,
        [field]: value
      }
    }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      country,
      state: '',
      region: ''
    }));
    setIndiaRegions([]);
  };

  const handleStateChange = (state) => {
    setFormData(prev => ({
      ...prev,
      state,
      region: ''
    }));
    if (state && formData.country === 'India') {
      fetchIndiaRegions(state);
    }
  };

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, { name: '' }]
    }));
  };

  const removePartner = (index) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.filter((_, i) => i !== index)
    }));
  };

  const handlePartnerChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      partners: prev.partners.map((partner, i) => 
        i === index ? { ...partner, name: value } : partner
      )
    }));
  };

  const addContactPerson = () => {
    setFormData(prev => ({
      ...prev,
      contactPersons: [
        ...prev.contactPersons,
        { title: '', name: '', phoneNumbers: '', email: '' }
      ]
    }));
  };

  const addAuthorizedSignatory = () => {
    setFormData(prev => ({
      ...prev,
      authorizedSignatories: [
        ...prev.authorizedSignatories,
        { 
          title: '', 
          name: '',
          age: '',
          designation: '',
          designationName: '', 
          fatherName: '',
          email: '',
          phone: '',
          address: ''
        }
      ]
    }));
  };

  const removeContactPerson = (index) => {
    setFormData(prev => ({
      ...prev,
      contactPersons: prev.contactPersons.filter((_, i) => i !== index)
    }));
  };

  const removeAuthorizedSignatory = (index) => {
    setFormData(prev => ({
      ...prev,
      authorizedSignatories: prev.authorizedSignatories.filter((_, i) => i !== index)
    }));
  };

  const renderFileUpload = (field) => {
    const document = formData.documents?.[field.name];
    const progress = uploadProgress[field.name];
    const error = errors[field.name];

    return (
      <div className="mb-4" key={field.name}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
        </label>
        <div className="flex items-center space-x-4">
          <PasteUploadArea
            onFileSelect={(file) => handleFileUpload(file, field.name, field.label)}
            accept={field.allowedTypes?.join(',') || "application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"}
            maxSize={field.maxSize || 5 * 1024 * 1024}
            className="flex-1"
            dragDropText="Upload a file"
            pasteText="or paste from clipboard (Ctrl+V)"
            uploadedUrl={document?.fileUrl || null}
          >
            
          </PasteUploadArea>
          
          {/* {document && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{document.fileName}</span>
              <a
                href={document?.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                View
              </a>
            </div>
          )} */}
          {progress !== undefined && progress < 100 && (
            <div className="w-24">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
        {errors[field.name] && (
          <p className="mt-1 text-sm text-red-600">{errors[field.name]}</p>
        )}
      </div>
    );
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  {record ? 'Edit Master Data' : 'Add New Master Data'}
                </h3>

                <div className="mt-4">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* User Selection for Admin */}
                    {user?.role === 'admin' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Created By (Optional)
                        </label>
                        <UserSelector
                          selectedUser={formData.createdBy}
                          onUserChange={handleUserChange}
                          userList={users}
                          placeholder="Select a user to assign as creator..."
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Type
                      </label>
                      <select
                        value={formData.clientType?._id || formData.clientType || ''}
                        onChange={(e) => handleInputChange(e, 'clientType')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        required
                      >
                        <option value="">Select Client Type</option>
                        {clientTypes.map(type => (
                          <option key={type._id} value={type._id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Name
                      </label>
                      <input
                        type="text"
                        value={formData.clientName}
                        onChange={(e) => handleInputChange(e, 'clientName')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Address
                      </label>
                      <textarea
                        value={formData.clientAddress}
                        onChange={(e) => handleInputChange(e, 'clientAddress')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        rows="3"
                        required
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Country Code
                        </label>
                        <select
                          value={formData.phoneNumber?.countryCode}
                          onChange={(e) => handlePhoneNumberChange('countryCode', e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        >
                          {phoneCountryCodes.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={formData.phoneNumber?.number}
                          onChange={(e) => handlePhoneNumberChange('number', e.target.value)}
                          placeholder="Enter phone number"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                        />
                      </div>
                    </div>

                    {/* Turnover */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Turnover
                      </label>
                      <select
                        value={formData.turnover === 0 ? '' : (formData.turnover < 20000000 ? 'less_than_2crore' : 'more_than_2crore')}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numericValue = value === 'less_than_2crore' ? 10000000 : value === 'more_than_2crore' ? 30000000 : 0;
                          handleInputChange({ target: { value: numericValue } }, 'turnover');
                        }}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value="">Select turnover</option>
                        <option value="less_than_2crore">Less than 2 Crore</option>
                        <option value="more_than_2crore">More than 2 Crore</option>
                      </select>
                    </div>

                    {/* UDYOG AADHAAR Certificate Requirement Check */}
                    {(() => {
                      const selectedClientType = clientTypes.find(type => type._id === (formData.clientType?._id || formData.clientType));
                      const entityTypeName = selectedClientType?.name || '';
                      const isLessThan2Crore = formData.turnover > 0 && formData.turnover < 20000000;
                      
                      // Define the entity types that require UDYOG AADHAAR
                      const udyogAadhaarEntityTypes = [
                        'Government Department',
                        'Statutory Body', 
                        'Society',
                        'Partnership Firm',
                        'Body Incorporate',
                        'Limited Liability Partnership'
                      ];
                      
                      // Check if the entity type requires UDYOG AADHAAR (case-insensitive)
                      const requiresUdyogAadhaar = isLessThan2Crore && udyogAadhaarEntityTypes.some(type => 
                        entityTypeName.toLowerCase() === type.toLowerCase()
                      );
                      
                      // Check if UDYOG AADHAAR document exists in master data (case-insensitive)
                      const hasUdyogAadhaar = Object.keys(formData.documents || {}).some(key => 
                        key.toLowerCase().includes('udyog') || key.toLowerCase().includes('aadhaar')
                      );
                      
                      if (requiresUdyogAadhaar) {
                        return (
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                {hasUdyogAadhaar ? (
                                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-gray-900">
                                  UDYOG AADHAAR Certificate
                                </h3>
                                <div className="mt-2 text-sm text-gray-700">
                                  {hasUdyogAadhaar ? (
                                    <p className="text-green-700">
                                      ✓ UDYOG AADHAAR Certificate found in master data
                                    </p>
                                  ) : (
                                    <p className="text-yellow-700">
                                      ⚠️ UDYOG AADHAAR Certificate is required for {entityTypeName} with turnover less than 2 Crore
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Country, State, Region */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <AutocompleteField
                          label="Country"
                          value={formData.country}
                          onChange={handleCountryChange}
                          options={countries}
                          placeholder="Type to search country..."
                          required={true}
                        />
                      </div>
                      <div>
                        <AutocompleteField
                          label="State"
                          value={formData.state}
                          onChange={handleStateChange}
                          options={formData.country === 'India' ? indiaStates : []}
                          placeholder={formData.country === 'India' ? "Type to search state..." : "Select country first"}
                          required={true}
                          disabled={formData.country !== 'India'}
                        />
                      </div>
                      <div>
                        <AutocompleteField
                          label="Region"
                          value={formData.region}
                          onChange={(region) => handleInputChange({ target: { value: region } }, 'region')}
                          options={indiaRegions}
                          placeholder={formData.state ? "Type to search region..." : "Select state first"}
                          required={true}
                          disabled={!formData.state || formData.country !== 'India'}
                        />
                      </div>
                    </div>

                    {/* Pincode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pincode
                      </label>
                      <input
                        type="text"
                        value={formData.pincode}
                        onChange={(e) => handleInputChange(e, 'pincode')}
                        placeholder="Enter pincode"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>

                    {/* Partners - Only show for Partnership Firm */}
                    {formData.clientType?.name === 'Partnership Firm' && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">Partners</h3>
                        {formData.partners.map((partner, index) => (
                          <div key={index} className="flex items-center space-x-4">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Partner Name {index + 1}
                              </label>
                              <input
                                type="text"
                                value={partner.name}
                                onChange={(e) => handlePartnerChange(index, e.target.value)}
                                placeholder="Enter partner name"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            {formData.partners.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removePartner(index)}
                                className="mt-6 text-red-600 hover:text-red-800 p-2"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addPartner}
                          className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                        >
                          Add Partner
                        </button>
                      </div>
                    )}

                    

                    {/* Document Upload Section */}
                    <div className="border-t pt-4">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">Documents</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {uploadFields.map(field => renderFileUpload(field))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Contact Persons</h3>
                      {formData.contactPersons.map((person, index) => (
                        <div key={index} className="p-4 border rounded-lg space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="lg:col-span-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Title
                              </label>
                              <select
                                value={person.title}
                                onChange={(e) => handleInputChange(e, 'title', index)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              >
                                <option value="">Select Title</option>
                                {['Mr', 'Miss', 'Mrs', 'Dr'].map(title => (
                                  <option key={title} value={title}>{title}</option>
                                ))}
                              </select>
                            </div>
                            <div className="lg:col-span-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Name
                              </label>
                              <input
                                type="text"
                                value={person.name}
                                onChange={(e) => handleInputChange(e, 'name', index)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            <div className="lg:col-span-1">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Numbers
                              </label>
                              <input
                                type="text"
                                value={person.phoneNumbers}
                                onChange={(e) => handleInputChange(e, 'phoneNumbers', index)}
                                placeholder="Comma separated numbers"
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            <div className="lg:col-span-1" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  value={person.email}
                                  onChange={(e) => handleInputChange(e, 'email', index)}
                                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                  required
                                />
                              </div>
                              {formData.contactPersons.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeContactPerson(index)}
                                  className="text-red-600 hover:text-red-800 p-2"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addContactPerson}
                        className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                      >
                        Add Contact Person
                      </button>
                    </div>

                    {/* Authorized Signatories Section */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">Authorized Signatories</h3>
                      {formData.authorizedSignatories.map((signatory, index) => (
                        <div key={index} className="p-4 border rounded-lg space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Title
                              </label>
                              <select
                                value={signatory.title}
                                onChange={(e) => handleInputChange(e, 'title', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              >
                                <option value="">Select Title</option>
                                {['Mr', 'Miss', 'Mrs', 'Dr'].map(title => (
                                  <option key={title} value={title}>{title}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Name
                              </label>
                              <input
                                type="text"
                                value={signatory.name}
                                onChange={(e) => handleInputChange(e, 'name', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Age
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="150"
                                value={signatory.age}
                                onChange={(e) => handleInputChange(e, 'age', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Designation
                              </label>
                              <input
                                type="text"
                                value={signatory.designation}
                                onChange={(e) => handleInputChange(e, 'designation', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Designation Name
                              </label>
                              <input
                                type="text"
                                value={signatory.designationName}
                                onChange={(e) => handleInputChange(e, 'designationName', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email
                              </label>
                              <input
                                type="email"
                                value={signatory.email}
                                onChange={(e) => handleInputChange(e, 'email', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone
                              </label>
                              <input
                                type="text"
                                value={signatory.phone}
                                onChange={(e) => handleInputChange(e, 'phone', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Father Name
                              </label>
                              <input
                                type="text"
                                value={signatory.fatherName}
                                onChange={(e) => handleInputChange(e, 'fatherName', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Address
                              </label>
                              <input
                                type="text"
                                value={signatory.address}
                                onChange={(e) => handleInputChange(e, 'address', index, 'authorizedSignatories')}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          {formData.authorizedSignatories.length > 1 && (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeAuthorizedSignatory(index)}
                                className="text-red-600 hover:text-red-800 p-2"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addAuthorizedSignatory}
                        className="w-full border-2 border-dashed border-gray-300 p-2 rounded-lg hover:border-gray-400"
                      >
                        Add Authorized Signatory
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.isActive}
                        onChange={(e) => handleInputChange(e, 'isActive')}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {loading ? 'Processing...' : record ? 'Update' : 'Create'}
                      </button>
                      <button
                        type="button"
                        onClick={onCancel}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterDataForm;